import React, { useEffect, useState, useRef } from "react";
import {
  Box,
  Typography,
  Grid,
  InputBase,
  IconButton,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Snackbar,
  Button,
  Alert,
  Collapse,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import Tooltip from "@mui/material/Tooltip";
import UndoIcon from "@mui/icons-material/Undo";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { ArrowBack, ArrowForward } from "@mui/icons-material";
import axios from "axios";
import { API_ENDPOINT_URL } from "../../util/api";
import { getLocalItem } from "../../util/helper";

const domainRegex = /^(?!:\/\/)([a-zA-Z0-9-_]+\.)+[a-zA-Z]{2,6}$/;
const BrandAndLogo = () => {
  const [loading, setLoading] = useState(false);
  const token = getLocalItem("key");
  const [currentApiUrl, setCurrentApiUrl] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [brandData, setBrandData] = useState([]);
  const [nextToken, setNextToken] = useState(null);
  const [prevToken, setPrevToken] = useState(null);
  const [prevTokens, setPrevTokens] = useState([]);
  const [total, setTotal] = useState(0);
  const [currentTotal, setCurrentTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [isPaging, setIsPaging] = useState(false);
  const [showInsert, setShowInsert] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [deleteIndex, setDeleteIndex] = useState(null);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [message, setMessage] = useState("");
  const [severity, setSeverity] = useState("error"); // 'error' | 'warning' | 'info' | 'success'
  const [newBrand, setNewBrand] = useState({
    domain: "",
    logos: [],
    files: [],
    isEditing: false,
  });
  const [isValidNewDomain, setIsValidNewDomain] = useState(false);
  const [isValidEditDomain, setIsValidEditDomain] = useState(true);

  const [openIndex, setOpenIndex] = useState(null);
  const [loadingIndex, setLoadingIndex] = useState(null);

  const fetchBrandDetails = async (brand, index) => {
    try {
      setLoadingIndex(index);
      const response = await axios.get(
        `${API_ENDPOINT_URL}/brand/detail/?domain=${brand.domain}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      setBrandData((prev) =>
        prev.map((b) =>
          b.domain === brand.domain ? { ...b, logos: response.data.logos } : b
        )
      );
    } catch (error) {
      console.error("Error fetching brand details:", error);
    } finally {
      setLoadingIndex(null);
    }
  };

  const handleExpandCollapseToggle = (brand, index) => {
    if (openIndex === index) {
      setOpenIndex(null); // Collapse if already open
    } else {
      setOpenIndex(index); // Expand this brand
      if (!brandData[brand.domain]) {
        fetchBrandDetails(brand, index);
      }
    }
  };

  const fetchBrandList = (url) => {
    axios
      .get(url, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((response) => {
        setBrandData(response.data.results);
        setNextToken(response.data.next_token || null);
        setCurrentApiUrl(url);
        setTotal(response.data.total);
        setCurrentTotal(response.data.page_size);
        setCurrentPage(1);
        if (response.data.total > response.data.page_size) {
          setIsPaging(true);
        } else {
          setIsPaging(false);
        }
        setPrevTokens([]);
        setPrevToken(null);
        setOpenIndex(null);
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => setLoading(false));
  };

  const handleSearch = () => {
    setLoading(true);

    const queryString = new URLSearchParams({ search: searchTerm }).toString();
    const url = `${API_ENDPOINT_URL}/brand/list/?${queryString}`;

    fetchBrandList(url);
  };

  const loadMoreData = async () => {
    if (!currentApiUrl && !nextToken) return;
    setLoading(true);
    try {
      setPrevTokens((prev) => [...prev, prevToken]);
      setPrevToken(nextToken);
      const response = await axios.get(currentApiUrl, {
        headers: { Authorization: `Bearer ${token}` },
        params: { next_token: nextToken },
      });
      setBrandData(response.data.results);
      setNextToken(response.data.next_token || null);
      setTotal(response.data.total);
      setCurrentTotal((prev) => prev + response.data.page_size);
      setCurrentPage((prev) => prev + 1);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setOpenIndex(null);
      setLoading(false);
    }
  };

  const loadPrevData = async () => {
    setLoading(true);
    try {
      const lastToken = prevTokens[prevTokens.length - 1];
      setPrevToken(lastToken);
      setPrevTokens((prev) => prev.slice(0, -1));
      const response = await axios.get(currentApiUrl, {
        headers: { Authorization: `Bearer ${token}` },
        params: { next_token: lastToken },
      });
      setBrandData(response.data.results);
      setNextToken(response.data.next_token || null);
      setTotal(response.data.total);
      setCurrentTotal((prev) => prev - response.data.page_size);
      setCurrentPage((prev) => prev - 1);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleEditToggle = async (brand, index) => {
    const foundBrand = brandData.find((b) => b.domain === brand.domain);
    if (foundBrand.logos.length === 0) {
      await fetchBrandDetails(brand, index); // Fetch only if not loaded
    }
    setBrandData((prev) =>
      prev.map((item, i) =>
        i === index
          ? {
              ...item,
              isEditing: !item.isEditing,
              editDomain: item.domain,
              editLogos: [...item.logos],
              newLogos: [],
              files: [],
              remove_objects: [],
            }
          : item
      )
    );
  };

  const handleNewToggle = () => {
    setShowInsert(false);
    setNewBrand({
      domain: "",
      logos: [],
      files: [],
      isEditing: false,
    });
  };

  const handleDomainChange = (index, newDomain) => {
    if (index > -1) {
      setBrandData((prev) =>
        prev.map((item, i) =>
          i === index ? { ...item, editDomain: newDomain } : item
        )
      );
      setIsValidEditDomain(domainRegex.test(newDomain));
    } else {
      setNewBrand((prev) => ({
        ...prev,
        domain: newDomain,
      }));
      setIsValidNewDomain(domainRegex.test(newDomain));
    }
  };

  const handleImageUpload = (index, event) => {
    const files = event.target.files;
    if (files.length > 0) {
      const newImages = Array.from(files).map((file) =>
        URL.createObjectURL(file)
      );
      if (index > -1) {
        setBrandData((prev) =>
          prev.map((item, i) =>
            i === index
              ? {
                  ...item,
                  newLogos: [...item.newLogos, ...newImages],
                  files: [...item.files, ...files],
                }
              : item
          )
        );
      } else {
        setNewBrand((prev) => ({
          ...prev,
          logos: [...prev.logos, ...newImages],
          files: [...prev.files, ...files],
        }));
      }
    }
  };

  const handleRemoveImage = (brandIndex, imageIndex, isNew = true) => {
    if (brandIndex > -1) {
      if (isNew) {
        setBrandData((prev) =>
          prev.map((item, i) =>
            i === brandIndex
              ? {
                  ...item,
                  newLogos: item.newLogos.filter(
                    (_, imgIdx) => imgIdx !== imageIndex
                  ),
                  files: item.files.filter(
                    (_, imgIdx) => imgIdx !== imageIndex
                  ),
                }
              : item
          )
        );
      } else {
        setBrandData((prev) =>
          prev.map((item, i) =>
            i === brandIndex
              ? {
                  ...item,
                  editLogos: item.editLogos.filter(
                    (_, imgIdx) => imgIdx !== imageIndex
                  ),
                  remove_objects: [
                    ...item.remove_objects,
                    item.editLogos[imageIndex]?.s3_key,
                  ],
                }
              : item
          )
        );
      }
    } else {
      setNewBrand((prev) => ({
        ...prev,
        logos: prev.logos.filter((_, i) => i !== imageIndex),
      }));
    }
  };

  const handleSubmit = (brand, index) => {
    if (index > -1) {
      setLoading(true);
      let editBrand = brandData[index];
      let isChangeDomain = false;
      const formData = new FormData();
      editBrand.files.forEach((file) => {
        formData.append("files", file);
      });
      formData.append("old_domain", editBrand.domain);
      if (editBrand.editDomain != editBrand.domain) {
        formData.append("new_domain", editBrand.editDomain);
        isChangeDomain = true;
      }
      if (editBrand.remove_objects.length > 0) {
        editBrand.remove_objects.forEach((object) => {
          formData.append("remove_objects", object);
        });
      }
      const url = `${API_ENDPOINT_URL}/brand/update/`;
      axios
        .put(url, formData, {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
        })
        .then((response) => {
          const data = response.data.results;
          if (isChangeDomain) {
            fetchBrandList(currentApiUrl);
          } else {
            if (editBrand.remove_objects.length > 0) {
              setBrandData((prev) =>
                prev.map((item, i) =>
                  i === index
                    ? {
                        ...item,
                        logos: item.logos.filter(
                          (logo, idx) =>
                            !editBrand.remove_objects.includes(logo.s3_key)
                        ),
                      }
                    : item
                )
              );
            }
            setBrandData((prev) =>
              prev.map((item, i) =>
                i === index
                  ? {
                      ...item,
                      domain: data.domain,
                      logos: [...item.logos, ...data.logos],
                    }
                  : item
              )
            );
            setLoading(false);
          }
          handleEditToggle(brand, index);
          handleShowSnackbar("Update Success", "success");
        })
        .catch((error) => {
          handleShowSnackbar(error.response.data.message, "error");
          setLoading(false);
        })
        .finally();
    } else {
      setLoading(true);
      const formData = new FormData();
      newBrand.files.forEach((file) => {
        formData.append("files", file);
      });
      formData.append("domain", newBrand.domain);
      const url = `${API_ENDPOINT_URL}/brand/register/`;
      axios
        .post(url, formData, {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
        })
        .then((response) => {
          handleNewToggle();
          handleShowSnackbar("Register Success", "success");
          fetchBrandList(currentApiUrl);
        })
        .catch((error) => {
          handleShowSnackbar(error.response.data.message, "error");
          setLoading(false);
        })
        .finally();
    }
  };

  const handleClickOpen = (index) => {
    setDeleteIndex(index);
    setOpenDialog(true);
  };

  const handleClose = () => {
    setOpenDialog(false);
    setDeleteIndex(null);
  };

  const handleDelete = () => {
    if (deleteIndex !== null) {
      setLoading(true);
      const itemToDelete = brandData[deleteIndex];
      const url = `${API_ENDPOINT_URL}/brand/delete/`;
      axios
        .delete(url, {
          headers: { Authorization: `Bearer ${token}` },
          data: {
            domain: itemToDelete.domain,
          },
        })
        .then((response) => {
          handleShowSnackbar("Delete Success", "success");
          fetchBrandList(currentApiUrl);
        })
        .catch((error) => {
          handleShowSnackbar(error.response.data.message, "error");
          setLoading(false);
        })
        .finally();

      setOpenDialog(false);
    }
  };

  const handleShowSnackbar = (msg, severity) => {
    setMessage(msg);
    setSeverity(severity);
    setOpenSnackbar(true);
  };

  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
  };

  useEffect(() => {
    setLoading(true);
    const url = `${API_ENDPOINT_URL}/brand/list/`;
    fetchBrandList(url);
    setPrevTokens([]);
  }, []);

  return (
    <Box
      mt="50px"
      sx={{
        width: "95%",
        textAlign: "center",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Box
        sx={{
          width: "100%",
          textAlign: "center",
        }}
      >
        <Grid container spacing={0}>
          <Grid item lg={6} sm={12} md={6}>
            <Box>
              <Typography
                variant="h1"
                fontSize={40}
                fontWeight="bold"
                color="#2699fb"
                textAlign="left"
              >
                Brands & Logos for Visual
              </Typography>
            </Box>
          </Grid>
          <Grid item lg={1} sm={0} md={0}></Grid>
        </Grid>
        <Box mt={5} width="100%">
          <Box width="98%" margin="auto">
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={9}>
                <InputBase
                  placeholder="Search for domain or brand"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  sx={{
                    width: "100%",
                    backgroundColor: "white",
                    border: "2px solid #2699fb",
                    borderRadius: "50px",
                    padding: "7px 16px 7px 16px",
                    fontSize: "15px",
                    color: "#42A7FF",
                  }}
                />
              </Grid>
              <Grid item xs={2}>
                <Tooltip title="Search">
                  <Typography
                    sx={{
                      borderRadius: "5px",
                      padding: "9px 17px 9px 17px",
                      backgroundColor: "#2699fb",
                      color: "white",
                      fontWeight: "bold",
                      fontSize: "17px",
                      cursor: "pointer",
                    }}
                    onClick={handleSearch}
                  >
                    Search
                  </Typography>
                </Tooltip>
              </Grid>
              <Grid item xs={1}>
                <Tooltip title="Add">
                  <IconButton
                    sx={{
                      backgroundColor: "#2699fb",
                      "&:hover": { backgroundColor: "blue" },
                    }}
                    onClick={() => setShowInsert(true)}
                  >
                    <AddIcon
                      sx={{ fontSize: 30, stroke: "white", strokeWidth: 3 }}
                    />
                  </IconButton>
                </Tooltip>
              </Grid>
            </Grid>
          </Box>
        </Box>
        {showInsert && (
          <Box mt={5} width="100%">
            <Box width="98%" margin="auto">
              <Grid container spacing={2} alignItems="center">
                <Grid
                  item
                  xs={9}
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                >
                  <InputBase
                    placeholder="Input domain"
                    value={newBrand.domain}
                    onChange={(e) => handleDomainChange(-1, e.target.value)}
                    sx={{
                      width: "100%",
                      backgroundColor: "white",
                      border: "2px solid",
                      borderRadius: "10px",
                      padding: "7px 16px 7px 16px",
                      fontSize: "15px",
                      color: "#AEB1B2",
                    }}
                  />
                  <Tooltip title="Cancel">
                    <IconButton>
                      <UndoIcon
                        onClick={() => handleNewToggle()}
                        sx={{ fontSize: 30, strokeWidth: 3 }}
                      />
                    </IconButton>
                  </Tooltip>
                </Grid>
                <Grid item xs={2}>
                  <Tooltip title="Submit">
                    <Typography
                      sx={{
                        borderRadius: "5px",
                        padding: "9px 17px 9px 17px",
                        backgroundColor: isValidNewDomain ? "#2699fb" : "gray",
                        color: "white",
                        fontWeight: "bold",
                        fontSize: "17px",
                        cursor: isValidNewDomain ? "pointer" : "not-allowed",
                        opacity: isValidNewDomain ? 1 : 0.5,
                      }}
                      onClick={() => isValidNewDomain && handleSubmit(newBrand, -1)}
                    >
                      Submit
                    </Typography>
                  </Tooltip>
                </Grid>
                <Grid item xs={1}>
                  <Tooltip title="Delete">
                    <IconButton onClick={() => handleNewToggle()}>
                      <CloseIcon
                        sx={{ fontSize: 30, stroke: "#AEB1B2", strokeWidth: 3 }}
                      />
                    </IconButton>
                  </Tooltip>
                </Grid>
              </Grid>
            </Box>
            <Box
              sx={{
                width: "100%",
                height: "2px",
                backgroundColor: "#AEB1B2",
                my: 1,
              }}
            />
            {/* Add Box */}
            <Box
              sx={{
                width: "100%",
                margin: "auto",
                mt: 3,
              }}
            >
              <Grid container spacing={2} alignItems="center">
                {/* Upload Box */}
                <Grid item xs={3}>
                  <Box
                    sx={{
                      width: "100%",
                      height: 150,
                      border: "2px solid #ddd",
                      borderRadius: "8px",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "pointer",
                      textAlign: "center",
                      backgroundColor: "#fff",
                      "&:hover": { borderColor: "#aaa" },
                    }}
                    onClick={() => document.getElementById("fileInput").click()}
                  >
                    <CloudUploadIcon sx={{ fontSize: 40, color: "black" }} />
                    <Typography variant="body2" sx={{ mt: 1, color: "black" }}>
                      Upload logo/icon
                    </Typography>
                    <input
                      id="fileInput"
                      type="file"
                      accept="image/*"
                      multiple
                      style={{ display: "none" }}
                      onChange={(event) => handleImageUpload(-1, event)}
                    />
                  </Box>
                </Grid>
                {/* List of uploaded images */}
                {newBrand.logos.map((logo, index) => (
                  <Grid item xs={3} key={index} sx={{ position: "relative" }}>
                    <Box
                      sx={{
                        width: "100%",
                        height: 150,
                        border: "2px solid #ddd",
                        borderRadius: "8px",
                        position: "relative",
                      }}
                    >
                      <img
                        src={logo}
                        alt={`upload-${index}`}
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "contain",
                          borderRadius: "8px",
                        }}
                      />
                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          sx={{
                            position: "absolute",
                            top: -10,
                            right: -10,
                            backgroundColor: "#2699fb",
                            color: "white",
                            "&:hover": { backgroundColor: "blue" },
                          }}
                          onClick={() => handleRemoveImage(-1, index)}
                        >
                          <CloseIcon
                            sx={{
                              fontSize: "medium",
                              stroke: "white",
                              strokeWidth: 3,
                            }}
                          />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Box>
          </Box>
        )}
        {/* List Box */}
        {loading ? (
          <Box
            sx={{
              position: "relative",
              width: "100%",
              height: "160px",
            }}
          >
            <Box
              sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
              }}
            >
              <CircularProgress sx={{ color: "#2699fb" }} />
            </Box>
          </Box>
        ) : (
          <Box width="100%" textAlign="center" mt={5}>
            {(brandData ?? []).map((brand, index) => (
              <Box key={index} width="98%" margin="auto" mb={4}>
                {!brand.isEditing ? (
                  <>
                    <Box
                      sx={{
                        width: "100%",
                        textAlign: "left",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <Typography variant="h6" fontWeight="bold">
                          {brand.domain}
                        </Typography>
                        <Tooltip title="Edit">
                          <IconButton onClick={() => handleEditToggle(brand, index)}>
                            <EditIcon sx={{ fontSize: 25, strokeWidth: 2 }} />
                          </IconButton>
                        </Tooltip>
                      </Box>

                      {/* Right Side: Expand/Collapse Button */}
                      <Tooltip
                        title={openIndex === index ? "Collapse" : "Expand"}
                      >
                        <IconButton
                          onClick={() =>
                            handleExpandCollapseToggle(brand, index)
                          }
                        >
                          {openIndex === index ? (
                            <ExpandLess sx={{ fontSize: 35 }} />
                          ) : (
                            <ExpandMore sx={{ fontSize: 35 }} />
                          )}
                        </IconButton>
                      </Tooltip>
                    </Box>
                    <Box
                      sx={{
                        width: "100%",
                        height: "2px",
                        backgroundColor: "#AEB1B2",
                        my: 1,
                      }}
                    />

                    <Collapse
                      in={openIndex === index}
                      timeout="auto"
                      unmountOnExit
                    >
                      <Box sx={{ width: "100%", margin: "auto", mt: 3 }}>
                      {loadingIndex === index ? (
                        <Box display="flex" justifyContent="center">
                          <CircularProgress />
                        </Box>
                      ) : (
                        <Grid container spacing={2} alignItems="center">
                          {/* List of uploaded images */}
                          {brand.logos.map((logo, index) => (
                            <Grid
                              item
                              xs={3}
                              key={index}
                              sx={{ position: "relative" }}
                            >
                              <Box
                                sx={{
                                  width: "100%",
                                  height: 150,
                                  border: "2px solid #ddd",
                                  borderRadius: "8px",
                                  position: "relative",
                                }}
                              >
                                <img
                                  src={logo.s3_url}
                                  alt={`upload-${index}`}
                                  style={{
                                    width: "100%",
                                    height: "100%",
                                    objectFit: "contain",
                                    borderRadius: "8px",
                                  }}
                                />
                              </Box>
                            </Grid>
                          ))}
                        </Grid>
                      )}
                      </Box>
                    </Collapse>
                  </>
                ) : (
                  <>
                    <Box width="98%" margin="auto">
                      <Grid container spacing={2} alignItems="center">
                        <Grid
                          item
                          xs={9}
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                        >
                          <InputBase
                            placeholder="Input domain"
                            value={brand.editDomain}
                            onChange={(e) =>
                              handleDomainChange(index, e.target.value)
                            }
                            sx={{
                              width: "100%",
                              backgroundColor: "white",
                              border: "2px solid",
                              borderRadius: "10px",
                              padding: "7px 16px 7px 16px",
                              fontSize: "15px",
                              color: "#AEB1B2",
                            }}
                          />
                          <IconButton onClick={() => handleEditToggle(brand, index)}>
                            {brand.isEditing ? (
                              <Tooltip title="Cancel">
                                <UndoIcon
                                  sx={{ fontSize: 30, strokeWidth: 3 }}
                                />
                              </Tooltip>
                            ) : (
                              <Tooltip title="Edit">
                                <EditIcon
                                  sx={{ fontSize: 30, strokeWidth: 3 }}
                                />
                              </Tooltip>
                            )}
                          </IconButton>
                        </Grid>
                        <Grid item xs={2}>
                          <Tooltip title="Submit">
                            <Typography
                              sx={{
                                borderRadius: "5px",
                                padding: "9px 17px 9px 17px",
                                backgroundColor: isValidEditDomain
                                  ? "#2699fb"
                                  : "gray",
                                color: "white",
                                fontWeight: "bold",
                                fontSize: "17px",
                                cursor: isValidEditDomain
                                  ? "pointer"
                                  : "not-allowed",
                                opacity: isValidEditDomain ? 1 : 0.5,
                              }}
                              onClick={() =>
                                isValidEditDomain && handleSubmit(brand, index)
                              }
                            >
                              Submit
                            </Typography>
                          </Tooltip>
                        </Grid>
                        <Grid item xs={1}>
                          <Tooltip title="Delete">
                            <IconButton onClick={() => handleClickOpen(index)}>
                              <CloseIcon
                                sx={{
                                  fontSize: 30,
                                  stroke: "#AEB1B2",
                                  strokeWidth: 3,
                                }}
                              />
                            </IconButton>
                          </Tooltip>
                        </Grid>
                      </Grid>
                    </Box>
                    <Box
                      sx={{
                        width: "100%",
                        height: "2px",
                        backgroundColor: "#AEB1B2",
                        my: 1,
                      }}
                    />
                    {/* Edit Box */}
                    <Box
                      sx={{
                        width: "100%",
                        margin: "auto",
                        mt: 3,
                      }}
                    >
                      <Grid container spacing={2} alignItems="center">
                        {/* Upload Box */}
                        <Grid item xs={3}>
                          <Box
                            sx={{
                              width: "100%",
                              height: 150,
                              border: "2px solid #ddd",
                              borderRadius: "8px",
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                              justifyContent: "center",
                              cursor: "pointer",
                              textAlign: "center",
                              backgroundColor: "#fff",
                              "&:hover": { borderColor: "#aaa" },
                            }}
                            onClick={() =>
                              document
                                .getElementById(`fileInput-${index}`)
                                .click()
                            }
                          >
                            <CloudUploadIcon
                              sx={{ fontSize: 40, color: "black" }}
                            />
                            <Typography
                              variant="body2"
                              sx={{ mt: 1, color: "black" }}
                            >
                              Upload logo/icon
                            </Typography>
                            <input
                              id={`fileInput-${index}`}
                              type="file"
                              accept="image/*"
                              multiple
                              style={{ display: "none" }}
                              onChange={(event) =>
                                handleImageUpload(index, event)
                              }
                            />
                          </Box>
                        </Grid>
                        {/* List of uploaded images */}
                        {brand.editLogos.map((logo, imgIndex) => (
                          <Grid
                            item
                            xs={3}
                            key={imgIndex}
                            sx={{ position: "relative" }}
                          >
                            <Box
                              sx={{
                                width: "100%",
                                height: 150,
                                border: "2px solid #ddd",
                                borderRadius: "8px",
                                position: "relative",
                              }}
                            >
                              <img
                                src={logo.s3_url}
                                alt={`upload-${imgIndex}`}
                                style={{
                                  width: "100%",
                                  height: "100%",
                                  objectFit: "contain",
                                }}
                              />
                              <Tooltip title="Delete">
                                <IconButton
                                  size="small"
                                  sx={{
                                    position: "absolute",
                                    top: -10,
                                    right: -10,
                                    backgroundColor: "#2699fb",
                                    color: "white",
                                    cursor: "pointer",
                                    padding: "4px",
                                    "&:hover": { backgroundColor: "blue" },
                                  }}
                                  onClick={() =>
                                    handleRemoveImage(index, imgIndex, false)
                                  }
                                >
                                  <CloseIcon
                                    sx={{
                                      fontSize: "medium",
                                      stroke: "white",
                                      strokeWidth: 3,
                                    }}
                                  />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </Grid>
                        ))}
                        {brand.newLogos.map((logo, imgIndex) => (
                          <Grid
                            item
                            xs={3}
                            key={imgIndex}
                            sx={{ position: "relative" }}
                          >
                            <Box
                              sx={{
                                width: "100%",
                                height: 150,
                                border: "2px solid #ddd",
                                borderRadius: "8px",
                                position: "relative",
                              }}
                            >
                              <img
                                src={logo}
                                alt={`upload-${imgIndex}`}
                                style={{
                                  width: "100%",
                                  height: "100%",
                                  objectFit: "contain",
                                }}
                              />
                              <Tooltip title="Delete">
                                <IconButton
                                  size="small"
                                  sx={{
                                    position: "absolute",
                                    top: -10,
                                    right: -10,
                                    backgroundColor: "#2699fb",
                                    color: "white",
                                    cursor: "pointer",
                                    padding: "4px",
                                    "&:hover": { backgroundColor: "blue" },
                                  }}
                                  onClick={() =>
                                    handleRemoveImage(index, imgIndex, true)
                                  }
                                >
                                  <CloseIcon
                                    sx={{
                                      fontSize: "medium",
                                      stroke: "white",
                                      strokeWidth: 3,
                                    }}
                                  />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </Grid>
                        ))}
                      </Grid>
                    </Box>
                  </>
                )}
              </Box>
            ))}
            {isPaging && (
              <Box
                sx={{
                  mt: 2,
                  display: "flex",
                  justifyContent: "center",
                  width: "100%",
                  gap: 2,
                }}
              >
                <IconButton
                  onClick={loadPrevData}
                  disabled={currentPage === 1}
                  sx={{ color: currentPage === 1 ? "gray" : "#2699fb" }}
                >
                  <ArrowBack sx={{ fontSize: 30, strokeWidth: 5 }} />
                </IconButton>
                <Typography
                  sx={{ fontSize: 30, fontWeight: "bold", color: "#2699fb" }}
                >
                  {currentTotal > total
                    ? `${total}/${total}`
                    : `${currentTotal}/${total}`}
                </Typography>
                <IconButton
                  onClick={loadMoreData}
                  disabled={!nextToken}
                  sx={{ color: nextToken ? "#2699fb" : "gray" }}
                >
                  <ArrowForward sx={{ fontSize: 30, strokeWidth: 5 }} />
                </IconButton>
              </Box>
            )}
          </Box>
        )}
        <Dialog open={openDialog} onClose={handleClose}>
          <DialogTitle>Confirm</DialogTitle>
          <DialogContent>
            Are you sure you want to delete {brandData[deleteIndex]?.domain}?
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose} color="primary">
              Cancel
            </Button>
            <Button onClick={handleDelete} color="error">
              Delete
            </Button>
          </DialogActions>
        </Dialog>
        <Snackbar
          open={openSnackbar}
          autoHideDuration={5000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <Alert onClose={handleCloseSnackbar} severity={severity}>
            {message}
          </Alert>
        </Snackbar>
      </Box>
    </Box>
  );
};

export default BrandAndLogo;
